import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { googleLogout, CredentialResponse } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';

// Define the shape of your UserInfo object, similar to what your backend returns
interface UserInfo {
  email: string;
}

interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  login: (credentialResponse: CredentialResponse) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  apiFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const validateSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUser({ email: data.user_email });
        setIsAuthenticated(true);
        localStorage.setItem('klinks_user_email', data.user_email);
      } else {
        handleSessionInvalid();
      }
    } catch (error) {
      console.error('Session validation error:', error);
      handleSessionInvalid();
    } finally {
      setIsLoading(false);
    }
  };

  // On mount, validate the session with the backend
  useEffect(() => {
    validateSession();
  }, []);

  const handleSessionInvalid = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('klinks_user_email');
    navigate('/login', { replace: true });
  };

  const apiFetch = async (url: string, options: RequestInit = {}) => {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
    });

    if (response.status === 401) {
      handleSessionInvalid();
      throw new Error('Session expired');
    }

    return response;
  };

  const login = async (credentialResponse: CredentialResponse) => {
    const idToken = credentialResponse.credential;
    if (!idToken) {
      setError("No ID token received from Google.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ google_id_token: idToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Backend authentication failed');
      }

      const data = await response.json();
      setUser({ email: data.user_email });
      setIsAuthenticated(true);
      localStorage.setItem('klinks_user_email', data.user_email);
      navigate('/klinks/list', { replace: true });
    } catch (err: any) {
      setError(err.message || 'An unknown login error occurred.');
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('klinks_user_email');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    googleLogout();
    handleSessionInvalid();
  };

  const value = { user, isAuthenticated, login, logout, isLoading, error, apiFetch };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
